import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import * as Sentry from '@sentry/react';
import {
  CollectionName,
  Conversation,
  DocumentObject,
  InitBotSite,
} from '@tomis-tech/types';

import { SessionObject } from '../types';
import { SITE_SLUG } from '../consts';
import { IS_DEV } from '../../consts';
import { SessionContext, CustomBotIconContext } from '../context';
import { logError, addBreadcrumb } from '.././sentry-iframe';
import { useCustomBotIcon } from '../../hooks/useCustomBotIcon';
import { FIREBASE, getFirebaseUserId } from '../../firebase';
import { transformFirebaseTimestampToJsDate } from '../utils';

type Props = {
  /** ID of logged in Firebase user (same as TOMIS device token) */
  firebaseUid: string;
  site: InitBotSite;
  conversationId: string;
  children: React.ReactNode;
};

/** Initialize the ChatBot */
export default function Initialize({
  firebaseUid,
  site,
  conversationId,
  children,
}: Props) {
  const firebaseUser = useFirebaseUser(firebaseUid);
  const conversation = useConversation(conversationId);

  /** Custom Bot Icon URL and loading status (if any) */
  const customBotIcon = useCustomBotIcon(site.botConfig.botIcon);

  /** Chatbot is ready to load on the page */
  const ready = Boolean(firebaseUser && conversation);

  /** Session Object to reference throughout ChatBot */
  const sessionObject: SessionObject = {
    site,
    conversation,
  };

  if (ready) {
    return (
      <SessionContext.Provider value={sessionObject}>
        <CustomBotIconContext.Provider value={customBotIcon}>
          {children}
        </CustomBotIconContext.Provider>
      </SessionContext.Provider>
    );
  }
  return null;
}

/*************************************************************************************/

/** Handle Auth for Firebase User. Firebase auth
 * must be successful before we can access Firebase, so
 * this hook should be used at the highest possible
 * location in the component tree.
 *
 * - Log in Firebase User if device ID is present
 * - If no token, but Firebase User still logged in,
 * log out Firebase User
 * - If no token & Firebase User NOT logged in, do nothing
 *
 * @param {string} tomisDeviceId Cross-site TOMIS Device ID, which will be used
 * to generate a token via TOMIS Fire microservices */
const useFirebaseUser = (tomisDeviceId: string) => {
  const [firebaseUser, setFirebaseUser] = useState<firebase.User | null>(null);

  useEffect(() => {
    return FIREBASE.auth.onAuthStateChanged(async firebaseUser => {
      addBreadcrumb({
        message: 'Firebase auth state changed',
        data: {
          isLoggedIn: Boolean(firebaseUser),
          firebaseUserId: firebaseUser ? firebaseUser.uid : undefined,
        },
      });

      if (firebaseUser) {
        // set local state
        setFirebaseUser(firebaseUser);
        if (!IS_DEV) {
          Sentry.withScope(scope => {
            scope.setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              phoneNumber: firebaseUser.phoneNumber,
              tomisDeviceId,
            });
          });
        }
      }
    });
  }, [tomisDeviceId]);

  return firebaseUser;
};

/** Get ChatBot Conversation from Firebase */
const useConversation = (
  conversationId: string
): DocumentObject<Conversation> => {
  const [conversation, setConversation] = useState<
    DocumentObject<Conversation>
  >(null);

  /** Logged in Firebase User UID */
  const firebaseUserId = getFirebaseUserId();

  useEffect(() => {
    if (conversationId && SITE_SLUG && firebaseUserId) {
      return FIREBASE.db
        .collection(CollectionName.Sites)
        .doc(SITE_SLUG)
        .collection(CollectionName.Conversations)
        .doc(conversationId)
        .onSnapshot(
          convoSnapshot => {
            if (!convoSnapshot.exists || !convoSnapshot.data()) {
              // Firebase connection might be disconnected, do nothing
              return;
            }

            const convoData = transformFirebaseTimestampToJsDate<Conversation>(
              convoSnapshot.data()
            );
            convoData.checkAvailability = convoData.checkAvailability
              ? transformFirebaseTimestampToJsDate(convoData.checkAvailability)
              : undefined;
            setConversation({ value: convoData, ref: convoSnapshot.ref });
          },
          err => {
            if (FIREBASE.auth.currentUser) {
              logError(err, {
                message: `User [${FIREBASE.auth.currentUser.uid}] failed to subscribe to conversation ${conversationId}`,
              });
            }
            // do nothing so that conversation can still show
            // setConversation(null);
          }
        );
    } else {
      // missing conversation id, site slug, or not logged into firebase
      setConversation(null);
    }
  }, [conversationId, firebaseUserId]);

  return conversation;
};
